import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { submitMicrosoftWaitlist } from "./waitlistService";
import "./MicrosoftWaitlist.css";

const EMPTY_FORM = {
  workEmail: "",
  fullName: "",
  companyName: "",
  notes: "",
};

const WAITLIST_QUERY_PARAM = "waitlist";
const WAITLIST_SOURCE_QUERY_PARAM = "waitlist_source";
const MICROSOFT_WAITLIST_VALUE = "microsoft";

const readWaitlistQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    shouldOpen: params.get(WAITLIST_QUERY_PARAM) === MICROSOFT_WAITLIST_VALUE,
    sourceOverride: params.get(WAITLIST_SOURCE_QUERY_PARAM)?.trim() || "",
  };
};

const clearWaitlistQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(WAITLIST_QUERY_PARAM) && !params.has(WAITLIST_SOURCE_QUERY_PARAM)) {
    return;
  }

  params.delete(WAITLIST_QUERY_PARAM);
  params.delete(WAITLIST_SOURCE_QUERY_PARAM);
  const nextSearch = params.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`
  );
};

/**
 * Build a webapp URL that auto-opens the Microsoft waitlist form.
 *
 * @example
 * buildMicrosoftWaitlistUrl("https://app.taskmantis.com", { source: "extension" });
 * // "https://app.taskmantis.com/?waitlist=microsoft&waitlist_source=extension"
 */
export const buildMicrosoftWaitlistUrl = (baseUrl, { source = "web" } = {}) => {
  const url = new URL(String(baseUrl || "").trim() || window.location.origin);
  url.searchParams.set(WAITLIST_QUERY_PARAM, MICROSOFT_WAITLIST_VALUE);
  if (source && source !== "web") {
    url.searchParams.set(WAITLIST_SOURCE_QUERY_PARAM, source);
  }
  return url.toString();
};

/**
 * Themed Microsoft waitlist form for the login surfaces.
 *
 * @example
 * <MicrosoftWaitlist source="web" />
 *
 * @example
 * <MicrosoftWaitlist
 *   source="extension"
 *   onOpenForm={() => openWebappTab(null, "https://app.taskmantis.com/?waitlist=microsoft")}
 * />
 */
const MicrosoftWaitlist = ({ source = "web", onOpenForm = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const submissionSource = readWaitlistQueryParams().sourceOverride || source;

  const resetFormState = () => {
    setForm(EMPTY_FORM);
    setError("");
    setSuccessMessage("");
  };

  const closeForm = () => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    clearWaitlistQueryParams();
  };

  const openForm = () => {
    resetFormState();
    if (onOpenForm) {
      onOpenForm();
      return;
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (onOpenForm) {
      return undefined;
    }

    if (!readWaitlistQueryParams().shouldOpen) {
      return undefined;
    }

    resetFormState();
    setIsOpen(true);
    return undefined;
  }, [onOpenForm]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        setIsOpen(false);
        clearWaitlistQueryParams();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSubmitting]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const workEmail = form.workEmail.trim();
    if (!workEmail) {
      setError("Work email is required.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitMicrosoftWaitlist({
        work_email: workEmail,
        full_name: form.fullName.trim(),
        company_name: form.companyName.trim(),
        notes: form.notes.trim(),
        source: submissionSource,
      });
      setSuccessMessage(response.message);
      setForm(EMPTY_FORM);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fullscreenForm = isOpen ? (
    <div
      className="microsoft-waitlist__fullscreen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="microsoft-waitlist-title"
    >
      <button
        type="button"
        className="microsoft-waitlist__close"
        onClick={closeForm}
        aria-label="Close waitlist form"
      >
        ×
      </button>

      <div className="microsoft-waitlist__layout">
        <div className="microsoft-waitlist__scroll">
          <div className="microsoft-waitlist__content">
            {successMessage ? (
              <div className="microsoft-waitlist__success">
                <h2 id="microsoft-waitlist-title">You&apos;re on the list</h2>
                <p>{successMessage}</p>
                <button
                  type="button"
                  className="microsoft-waitlist__submit"
                  onClick={closeForm}
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <>
                <div className="microsoft-waitlist__header">
                  <h2 id="microsoft-waitlist-title">Join the Microsoft waitlist</h2>
                  <p>
                    We&apos;ll email you when Microsoft sign-in is available for
                    work accounts.
                  </p>
                </div>

                <form
                  id="microsoft-waitlist-form"
                  className="microsoft-waitlist__form"
                  onSubmit={handleSubmit}
                >
                  <label className="microsoft-waitlist__field">
                    <span>
                      Work email <span className="microsoft-waitlist__required">*</span>
                    </span>
                    <input
                      type="email"
                      className="microsoft-waitlist__input"
                      value={form.workEmail}
                      onChange={(event) => updateField("workEmail", event.target.value)}
                      placeholder="you@company.com"
                      autoComplete="email"
                      required
                    />
                  </label>

                  <label className="microsoft-waitlist__field">
                    <span>Full name</span>
                    <input
                      type="text"
                      className="microsoft-waitlist__input"
                      value={form.fullName}
                      onChange={(event) => updateField("fullName", event.target.value)}
                      placeholder="Alex Kim"
                      autoComplete="name"
                    />
                  </label>

                  <label className="microsoft-waitlist__field">
                    <span>Company name</span>
                    <input
                      type="text"
                      className="microsoft-waitlist__input"
                      value={form.companyName}
                      onChange={(event) => updateField("companyName", event.target.value)}
                      placeholder="Contoso"
                      autoComplete="organization"
                    />
                  </label>

                  <label className="microsoft-waitlist__field">
                    <span>Anything else we should know?</span>
                    <textarea
                      className="microsoft-waitlist__textarea"
                      value={form.notes}
                      onChange={(event) => updateField("notes", event.target.value)}
                      placeholder="Optional context for the team"
                      rows={4}
                    />
                  </label>
                </form>
              </>
            )}
          </div>
        </div>

        {!successMessage ? (
          <div className="microsoft-waitlist__footer">
            {error ? <p className="microsoft-waitlist__error">{error}</p> : null}
            <button
              type="submit"
              form="microsoft-waitlist-form"
              className="microsoft-waitlist__submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting…" : "Join waitlist"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <>
      <p className="microsoft-waitlist__trigger">
        Work account on Microsoft?{" "}
        <button
          type="button"
          className="microsoft-waitlist__trigger-button"
          onClick={openForm}
        >
          Join the waitlist
        </button>
      </p>

      {!onOpenForm && fullscreenForm ? createPortal(fullscreenForm, document.body) : null}
    </>
  );
};

export default MicrosoftWaitlist;
